import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import { FileText, Loader2, Check, Circle, Trash2, Upload, Edit2, Save, X, Lock, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function SessionNotes({ sessionNotes, onUpload, selectedNote, onPublishToggle, onDelete, onUpdate, isClientView = false }) {
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [uploading, setUploading] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [saving, setSaving] = useState(false);

  // Private notes state
  const [privateNote, setPrivateNote] = useState('');
  const [privateNoteId, setPrivateNoteId] = useState(null);
  const [showPrivateNote, setShowPrivateNote] = useState(false);
  const [savingPrivateNote, setSavingPrivateNote] = useState(false);
  const privateNoteSaveTimeout = useRef(null);

  useEffect(() => {
    if (selectedNote) {
      setEditedContent(selectedNote.content || '');
      setIsEditing(false);
      // Fetch private note for this session
      if (!isClientView) {
        fetchPrivateNote(selectedNote.id);
      }
    }
  }, [selectedNote, isClientView]);

  // Auto-save private note with debouncing
  useEffect(() => {
    if (privateNoteSaveTimeout.current) {
      clearTimeout(privateNoteSaveTimeout.current);
    }

    if (selectedNote && privateNote !== null && !isClientView) {
      privateNoteSaveTimeout.current = setTimeout(() => {
        savePrivateNote();
      }, 1000); // Save after 1 second of no typing
    }

    return () => {
      if (privateNoteSaveTimeout.current) {
        clearTimeout(privateNoteSaveTimeout.current);
      }
    };
  }, [privateNote]);

  const fetchPrivateNote = async (sessionNoteId) => {
    try {
      const response = await axios.get(`${API_URL}/session-notes/${sessionNoteId}/private-note`);
      if (response.data) {
        setPrivateNote(response.data.content || '');
        setPrivateNoteId(response.data.id);
      } else {
        setPrivateNote('');
        setPrivateNoteId(null);
      }
    } catch (error) {
      console.error('Error fetching private note:', error);
      setPrivateNote('');
      setPrivateNoteId(null);
    }
  };

  const savePrivateNote = async () => {
    if (!selectedNote) return;

    try {
      setSavingPrivateNote(true);
      const response = await axios.put(`${API_URL}/session-notes/${selectedNote.id}/private-note`, {
        content: privateNote
      });
      setPrivateNoteId(response.data.id);
    } catch (error) {
      console.error('Error saving private note:', error);
      toast.error('Failed to save private note');
    } finally {
      setSavingPrivateNote(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (noteContent.trim()) {
      setUploading(true);
      try {
        await onUpload(noteContent);
        setNoteContent('');
        setShowUploadForm(false);
        toast.success('Session recap uploaded successfully!');
      } catch (error) {
        toast.error('Error uploading session recap. Please try again.');
      } finally {
        setUploading(false);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePublishToggle = async () => {
    if (!selectedNote) return;

    setToggling(true);
    try {
      // SQLite returns booleans as 0/1, so we need to check explicitly
      const currentPublished = selectedNote.published === 1 || selectedNote.published === true;
      const newPublishedStatus = !currentPublished;

      await axios.put(`${API_URL}/session-notes/${selectedNote.id}/publish`, {
        is_published: newPublishedStatus
      });

      // Call the callback to refresh data - this will update selectedNote via parent component
      if (onPublishToggle) {
        await onPublishToggle();
      }
    } catch (error) {
      console.error('Error toggling publish status:', error);
      console.error('Error response:', error.response?.data);
      toast.error(`Failed to update publish status: ${error.response?.data?.error || error.message}`);
    } finally {
      setToggling(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditedContent(selectedNote.content || '');
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedContent(selectedNote.content || '');
  };

  const handleSaveEdit = async () => {
    if (!selectedNote) return;

    setSaving(true);
    try {
      await axios.put(`${API_URL}/session-notes/${selectedNote.id}`, {
        content: editedContent
      });

      setIsEditing(false);
      
      // Call the callback to refresh data
      if (onUpdate) {
        await onUpdate();
      }
    } catch (error) {
      console.error('Error updating session recap:', error);
      toast.error('Failed to update session recap');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedNote) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete this session recap from ${selectedNote.session_date || formatDate(selectedNote.created_at)}? This action cannot be undone.`
    );

    if (!confirmed) return;

    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/session-notes/${selectedNote.id}`);

      // Call the callback to refresh data
      if (onDelete) {
        await onDelete();
      }
    } catch (error) {
      console.error('Error deleting session note:', error);
      toast.error('Failed to delete session recap');
    } finally {
      setDeleting(false);
    }
  };

  // Detail view when a specific note is selected
  if (selectedNote) {
    return (
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6" style={{ boxShadow: 'var(--shadow-md)' }}>
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-2">Session Notes</h3>
            <p className="text-sm text-[var(--text-tertiary)]">{selectedNote.session_date || formatDate(selectedNote.created_at)}</p>
          </div>
          {!isClientView && (onPublishToggle || onDelete || onUpdate) && (
            <div className="flex items-center gap-2">
              {onUpdate && !isEditing && (
                <button
                  onClick={handleEdit}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 bg-[var(--accent)]/20 text-[var(--accent)] border border-[var(--accent)]/30 hover:bg-[var(--accent)]/30"
                >
                  <Edit2 className="w-4 h-4" />
                  <span>Edit</span>
                </button>
              )}
              {isEditing && (
                <>
                  <button
                    onClick={handleSaveEdit}
                    disabled={saving}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 bg-green-600/20 text-green-400 border border-green-600/30 hover:bg-green-600/30 disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Save</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={saving}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 bg-[var(--bg-tertiary)] text-[var(--text-secondary)] border border-[var(--border-color)] hover:bg-[var(--bg-primary)] disabled:opacity-50"
                  >
                    <X className="w-4 h-4" />
                    <span>Cancel</span>
                  </button>
                </>
              )}
              {onPublishToggle && !isEditing && (
                <button
                  onClick={handlePublishToggle}
                  disabled={toggling}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    (selectedNote.published === 1 || selectedNote.published === true)
                      ? 'bg-[var(--success-bg)] text-[var(--success)] border border-[var(--success)]/30 hover:bg-[var(--success)]/20'
                      : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] border border-[var(--border-color)] hover:bg-[var(--bg-primary)]'
                  } disabled:opacity-50`}
                >
                  {toggling ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Updating...</span>
                    </>
                  ) : (selectedNote.published === 1 || selectedNote.published === true) ? (
                    <>
                      <Check className="w-4 h-4" />
                      <span>Published</span>
                    </>
                  ) : (
                    <>
                      <Circle className="w-4 h-4" />
                      <span>Unpublished</span>
                    </>
                  )}
                </button>
              )}
              {onDelete && !isEditing && (
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 bg-red-600/20 text-red-400 border border-red-600/30 hover:bg-red-600/30 disabled:opacity-50"
                >
                  {deleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      <span>Delete</span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
        {isEditing ? (
          <div className="bg-[var(--bg-primary)] p-6 rounded-lg border border-[var(--border-color)]" style={{ boxShadow: 'var(--shadow-sm)' }}>
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="w-full bg-[var(--bg-primary)] text-[var(--text-primary)] px-4 py-3 rounded-lg border border-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/50 focus:border-[var(--accent)]/50 min-h-[400px] font-mono text-sm resize-none"
              style={{ color: 'var(--text-primary)' }}
              placeholder="Edit session recap content..."
            />
          </div>
        ) : (
          <div className="text-base bg-[var(--bg-primary)] p-6 rounded-lg leading-relaxed prose prose-invert max-w-none" style={{ boxShadow: 'var(--shadow-sm)', color: 'var(--text-primary)' }}>
            <ReactMarkdown
              components={{
                h1: ({node, ...props}) => <h1 className="font-bold mb-3 mt-4 text-[var(--text-primary)] text-2xl" {...props} />,
                h2: ({node, ...props}) => <h2 className="font-semibold mb-2 mt-4 text-[var(--text-primary)] text-xl" {...props} />,
                h3: ({node, ...props}) => <h3 className="font-semibold mb-2 mt-3 text-[var(--accent)] text-lg" {...props} />,
                h4: ({node, ...props}) => <h4 className="font-medium mb-2 mt-3 text-[var(--text-secondary)] text-base" {...props} />,
                p: ({node, ...props}) => <p className="text-[var(--text-primary)] mb-3 leading-relaxed" {...props} />,
                strong: ({node, ...props}) => <strong className="font-semibold text-[var(--text-primary)]" {...props} />,
                em: ({node, ...props}) => <em className="italic text-[var(--text-secondary)]" {...props} />,
                ul: ({node, ...props}) => <ul className="list-disc mb-3 ml-4 space-y-2 text-[var(--text-primary)]" {...props} />,
                ol: ({node, ...props}) => <ol className="list-decimal mb-3 ml-4 space-y-2 text-[var(--text-primary)]" {...props} />,
                li: ({node, ...props}) => <li className="text-[var(--text-primary)] leading-relaxed pl-1" {...props} />,
                code: ({node, ...props}) => <code className="bg-[var(--bg-secondary)] px-1.5 py-0.5 rounded text-[var(--accent)] text-sm" {...props} />,
                blockquote: ({node, ...props}) => <blockquote className="border-l-2 border-[var(--accent)]/50 pl-4 italic text-[var(--text-secondary)] my-3" {...props} />,
                br: ({node, ...props}) => <br {...props} />,
              }}
            >
              {selectedNote.content ? selectedNote.content
                // Convert single line breaks to markdown line breaks (two spaces + newline)
                .replace(/\n(?!\n)/g, '  \n')
                // Ensure double line breaks create paragraphs
                .replace(/\n\n+/g, '\n\n') : ''}
            </ReactMarkdown>
          </div>
        )}

        {/* Private Coach Notes Section - Only visible to coach */}
        {!isClientView && (
          <div className="mt-6 bg-[var(--bg-tertiary)] rounded-lg p-4" style={{ border: '2px solid var(--accent)', opacity: 0.85, boxShadow: 'var(--shadow-sm)' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-[var(--accent)]" />
                <h4 className="text-sm font-semibold text-[var(--accent)]">
                  Coach-Only Notes (Client will never see this)
                </h4>
              </div>
              <div className="flex items-center gap-3">
                {savingPrivateNote && (
                  <span className="text-xs text-[var(--text-tertiary)] flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Saving...
                  </span>
                )}
                <button
                  onClick={() => setShowPrivateNote(!showPrivateNote)}
                  className="text-xs text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors flex items-center gap-1"
                >
                  {showPrivateNote ? (
                    <>
                      <EyeOff className="w-3 h-3" />
                      Hide
                    </>
                  ) : (
                    <>
                      <Eye className="w-3 h-3" />
                      Show
                    </>
                  )}
                </button>
              </div>
            </div>
            {showPrivateNote && (
              <textarea
                value={privateNote}
                onChange={(e) => setPrivateNote(e.target.value)}
                placeholder="Private coaching notes, counter-transference reactions, supervision thoughts, hypotheses...&#10;&#10;These notes are automatically saved and never shown to the client."
                className="w-full px-3 py-2 rounded-lg resize-none focus:outline-none focus:ring-2 font-mono text-sm h-32"
                style={{
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  boxShadow: 'var(--shadow-sm)',
                  focusRing: '2px solid var(--accent)'
                }}
              />
            )}
          </div>
        )}
      </div>
    );
  }

  // Overview when no specific note is selected
  return (
    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6" style={{ boxShadow: 'var(--shadow-md)' }}>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-2xl font-bold text-[var(--text-primary)] flex items-center gap-2.5">
            <FileText className="w-6 h-6" />
            <span>Session Notes</span>
          </h3>
          <p className="text-sm text-[var(--text-tertiary)] mt-1">
            {onUpload ? 'Upload and manage session notes' : 'View your published session notes'}
          </p>
        </div>
        {!isClientView && onUpload && (
          <button
            onClick={() => setShowUploadForm(!showUploadForm)}
            className="bg-[var(--accent)] hover:bg-[var(--accent)] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all"
          >
            {showUploadForm ? 'Cancel' : '+ Add Session'}
          </button>
        )}
      </div>

      {showUploadForm && onUpload && (
        <form onSubmit={handleSubmit} className="mb-6 p-5 bg-[var(--bg-primary)] rounded-lg border border-[var(--border-color)]" style={{ boxShadow: 'var(--shadow-sm)' }}>
          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">Paste your session transcript:</label>
          <textarea
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            className="w-full bg-[var(--bg-primary)] text-[var(--text-primary)] px-4 py-3 rounded-lg border border-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/50 focus:border-[var(--accent)]/50 h-48 resize-none text-sm font-medium"
            style={{ color: 'var(--text-primary)' }}
            placeholder="Paste session notes from Google Docs..."
            required
          />
          <button
            type="submit"
            disabled={uploading}
            className="w-full mt-3 bg-[var(--accent)] hover:bg-[var(--accent)] text-white px-4 py-3 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                <span>Upload Session Notes</span>
              </>
            )}
          </button>
        </form>
      )}

      {!showUploadForm && sessionNotes.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 mx-auto mb-4 text-[var(--text-tertiary)] opacity-50" />
          <p className="text-[var(--text-secondary)] text-lg mb-2">No session recaps yet</p>
          {onUpload ? (
            <p className="text-[var(--text-tertiary)] text-sm">Click "+ Add Session" to upload your first recap</p>
          ) : (
            <p className="text-[var(--text-tertiary)] text-sm">Your coach hasn't published any session recaps yet</p>
          )}
        </div>
      )}

      {!showUploadForm && sessionNotes.length > 0 && (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 mx-auto mb-4 text-[var(--text-tertiary)] opacity-50" />
          <p className="text-[var(--text-secondary)] text-lg mb-2">Select a session from the left</p>
          <p className="text-[var(--text-tertiary)] text-sm">View session details and transcripts</p>
        </div>
      )}
    </div>
  );
}

export default SessionNotes;
